import {
  type PigmentAuthoringSnapshot,
  type PigmentClient,
  type PigmentCommittedMutation,
  type PigmentInspectionAcceptanceReceipt,
  type PigmentPreparedMutation,
  type PigmentPublished,
} from "@pigment/client";
import {
  canonicalPigmentJson,
  type PigmentDocumentReconcileResult,
} from "@pigment/core";
import { desiredDocumentFromJson, desiredDocumentSource } from "./domain.js";

type SuccessfulReconciliation = Extract<PigmentDocumentReconcileResult, { ok: true }>;

export type ConsoleAuthoringClient = Pick<PigmentClient,
  | "authoringSnapshot"
  | "reconcileSnapshot"
  | "prepare"
  | "commitPrepared"
  | "publishPrepared"
>;

export class ConsoleWorkflowError extends Error {
  readonly code: "missing_authoring_base" | "no_effective_change" | "prepared_lineage_mismatch";

  constructor(code: ConsoleWorkflowError["code"], message: string) {
    super(message);
    this.name = "ConsoleWorkflowError";
    this.code = code;
  }
}

/**
 * Any desired-document edit invalidates every authority object derived from
 * its previous bytes. Keeping this transition explicit prevents a prepared or
 * accepted preview from surviving an advanced or guided post-prepare edit.
 */
export function desiredDocumentEdited(desiredSource: string) {
  return {
    desiredSource,
    reconciliation: null,
    prepared: null,
    inspectionJob: null,
    inspectionAcceptance: null,
    inspectionFailure: null,
    inspectionReport: null,
    committed: null,
    published: null,
  } as const;
}

/** Read one host-verified base and initialize a complete desired document. */
export async function readDesiredAuthoring(
  client: ConsoleAuthoringClient,
  page: string,
): Promise<{ snapshot: PigmentAuthoringSnapshot; desiredSource: string }> {
  const snapshot = await client.authoringSnapshot(page);
  try {
    return { snapshot, desiredSource: desiredDocumentSource(snapshot) };
  } catch (error) {
    throw new ConsoleWorkflowError(
      "missing_authoring_base",
      error instanceof Error ? error.message : "The host did not provide an authoring base.",
    );
  }
}

/** Compile desired state through the public client; this remains GET-only. */
export async function reconcileDesiredAuthoring(
  client: ConsoleAuthoringClient,
  snapshot: PigmentAuthoringSnapshot,
  desiredSource: string,
): Promise<PigmentDocumentReconcileResult> {
  return client.reconcileSnapshot(snapshot, desiredDocumentFromJson(desiredSource));
}

/**
 * Cross from compilation to preview authority using the exact mutation the
 * reconciler produced. Empty mutations remain explicit no-ops and never create
 * preview churn.
 */
export async function prepareDesiredAuthoring(
  client: ConsoleAuthoringClient,
  page: string,
  reconciliation: SuccessfulReconciliation,
): Promise<PigmentPreparedMutation> {
  if (reconciliation.mutation.operations.length === 0) {
    throw new ConsoleWorkflowError("no_effective_change", "The desired document already matches the current authoring base; there is nothing to prepare.");
  }
  const prepared = await client.prepare(page, reconciliation.mutation);
  if (canonicalPigmentJson(prepared.mutation) !== canonicalPigmentJson(reconciliation.mutation)) {
    throw new ConsoleWorkflowError("prepared_lineage_mismatch", "The prepared preview did not preserve the exact reconciled mutation.");
  }
  return prepared;
}

/** Preserve the exact accepted preview lineage at the draft mutation boundary. */
export function commitDesiredAuthoring(
  client: ConsoleAuthoringClient,
  prepared: PigmentPreparedMutation,
  acceptance: PigmentInspectionAcceptanceReceipt,
): Promise<PigmentCommittedMutation> {
  return client.commitPrepared(prepared, acceptance);
}

/** Publication consumes the exact committed receipt; it never reconstructs it. */
export function publishDesiredAuthoring(
  client: ConsoleAuthoringClient,
  committed: PigmentCommittedMutation,
): Promise<PigmentPublished> {
  return client.publishPrepared(committed);
}
